<template>
    <div class="fill-the-gaps-container">
        <div class="titles">
            <p class="title">FILL IN THE GAPS</p>
            <p class="subtitle">Here's what's missing from the links you shared:</p>
        </div>
        <div class="venue-info">
            <p class="title">CONFIRM LOCATION</p>
            <div class="info">
                <div ref="google-map" id="google-map"></div>
                <div class="fields">
                    <div class="location-input">
                        <input
                            ref="autocomplete"
                            v-model="formData.location"
                            class="form-control"
                            :class="formErrors.location ? 'is-invalid' : ''"
                            name="location"
                            type="text"
                        />
                        <div class="invalid-feedback">
                            You must enter a valid address to continue.
                        </div>
                    </div>
                    <div class="venueName-input">
                        <label for="venueName">Venue Name</label>
                        <input
                            v-model="formData.venueName"
                            class="form-control"
                            :class="formErrors.venueName ? 'is-invalid' : ''"
                            name="venueName"
                            type="text"
                        />
                        <div class="invalid-feedback">
                            You must enter your venue name to continue.
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="media-upload">
            <div v-if="formData.medias.length" class="view-carousel">
                <carousel :mediaList="formData.medias">
                    <p class="title"><a class="nostyle" href="">VIEW GALLERY</a></p>
                </carousel>
            </div>
            <input
                ref="uploadBtn"
                @change="handleMediaUpload"
                id="file-upload"
                type="file"
                accept="video/*,image/*"
                hidden="hidden"
            />
            <p v-if="formData.medias.length">
                We could import some media files from your socials
            </p>
            <button @click="handleUploadClick">UPLOAD NEW IMAGES AND VIDEOS</button>
            <p v-if="!formData.medias.length" class="required-message">
                Upload at least one image or video in order to continue
            </p>
        </div>
        <div class="step-action">
            <button :disabled="isContinueDisabled" @click="handleNextStep" class="next-step">
                CONTINUE
            </button>
            <button @click="handlePrevStep" class="prev-step">GO BACK</button>
        </div>
    </div>
</template>
<script>
    import Google from '@/constants/google'
    import Carousel from '@/components/ui/carousels/Carousel.vue'

    const inputValid = {
        location: (val) => !!val,
        venueName: (val) => !!val
    }

    export default {
        name: 'FillTheGaps',

        props: {
            defaultVals: {
                type: Object,
                required: false,
                default: null
            }
        },

        components: {
            carousel: Carousel
        },

        data: () => ({
            formData: {
                location: '',
                venueName: '',
                lat: null,
                lng: null,
                medias: []
            },

            formErrors: {
                location: false,
                venueName: false
            },

            mapData: {
                map: null,
                autocomplete: null
            },

            apiLoaded: false,
            checkInterval: null
        }),

        methods: {
            // NOTE: this needs to be the same name as callback defined in GoogleMapsApiController.php
            initialize() {
                // eslint-disable-next-line
                this.mapData.map = new google.maps.Map(this.$refs['google-map'], Google.DEFAULT_MAP_OPT)

                // eslint-disable-next-line
                this.mapData.autocomplete = new google.maps.places.Autocomplete(
                    this.$refs['autocomplete'],
                    {
                        fields: ['geometry', 'formatted_address', 'name'],
                        types: ['geocode', 'establishment']
                    }
                )

                this.mapData.autocomplete.addListener('place_changed', this.onPlaceChanged)
            },

            onPlaceChanged() {
                let place = this.mapData.autocomplete.getPlace()

                if (place.geometry && place.formatted_address) {
                    let lat = place.geometry.location.lat()
                    let lng = place.geometry.location.lng()

                    this.mapData.map.panTo({ lat, lng })

                    this.formData.location = place.formatted_address
                    this.formData.lat = lat
                    this.formData.lng = lng
                    this.formData.venueName = this.getVenueName(
                        place.formatted_address,
                        place.name
                    )
                }
            },

            getVenueName(full_addr, name) {
                if (!name || !full_addr) return ''

                let streetComponent = full_addr.split(',')[0].split(' ').slice(-1)[0]

                if (name.includes(streetComponent)) return ''

                return name
            },

            validate() {
                let keys = Object.keys(this.formErrors)
                let isInputValid = true

                for (let i = 0; i < keys.length; i++) {
                    if (!inputValid[keys[i]](this.formData[keys[i]])) {
                        this.formErrors[keys[i]] = true
                        isInputValid = false
                    } else {
                        this.formErrors[keys[i]] = false
                    }
                }

                return isInputValid
            },

            handleNextStep() {
                if (this.validate()) {
                    this.$emit('next', this.formData)
                }
                this.$emit('next', false)
            },

            handlePrevStep() {
                this.$emit('prev')
            },

            handleUploadClick() {
                this.$refs.uploadBtn.click()
            },

            handleMediaUpload(e) {
                if (!e.target.files.length) return

                this.$set(this.formData.medias, this.formData.medias.length, e.target.files[0])
            }
        },

        computed: {
            isContinueDisabled: (self) => {
                return !self.formData.medias.length
            }
        },

        watch: {
            apiLoaded: function (loaded) {
                if (loaded) {
                    clearInterval(this.checkInterval)
                }
            },

            'formData.location': function (val) {
                if (!val) {
                    this.formErrors.location = true
                } else {
                    this.formErrors.location = false
                }
            },
            'formData.venueName': function (val) {
                if (!val) {
                    this.formErrors.venueName = true
                } else {
                    this.formErrors.venueName = false
                }
            }
        },

        mounted() {
            this.checkInterval = setInterval(() => {
                if (window.google) {
                    this.apiLoaded = true
                    this.initialize()
                }
            })
        }
    }
</script>
<style lang="scss" scoped>
    .fill-the-gaps-container {
        flex: 0 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 50px;

        .titles {
            order: 1;
            flex: 0 0 auto;

            .title {
                font-family: 'Instrument Sans';
                font-style: normal;
                font-weight: 600;
                font-size: 21px;
                line-height: 24px;
                letter-spacing: 3px;
                text-transform: uppercase;
                color: white;
                text-align: center;
            }

            .subtitle {
                margin-top: 15px;
                font-family: 'Instrument Sans';
                font-style: normal;
                font-weight: 400;
                font-size: 18px;
                line-height: 27px;
                letter-spacing: 3px;
                color: white;
                text-align: center;
            }
        }

        .venue-info {
            order: 2;
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            margin-top: 60px;

            .title {
                order: 1;
                flex: 0 0 auto;
                font-family: 'Instrument Sans';
                font-style: normal;
                font-weight: 500;
                font-size: 18px;
                line-height: 22px;
                text-align: center;
                letter-spacing: 0.1em;
                text-transform: uppercase;
                font-feature-settings: 'calt' off;
                color: white;
            }

            .info {
                order: 2;
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: flex-start;
                gap: 70px;
                flex-wrap: wrap;

                #google-map {
                    width: 485px;
                    height: 416px;
                    border-radius: 15px;
                }

                .fields {
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    min-width: 300px;
                    max-width: 607px;
                    width: 100%;
                    flex: 1 1 607px;

                    .venueName-input {
                        margin-top: 50px;
                    }

                    input {
                        width: 607px;
                        height: 63px;
                        border: 1px solid #ffffff;
                        border-radius: 50px;
                        background: transparent;
                        color: white;
                        padding-left: 35px;
                        padding-left: 70px;
                        vertical-align: middle;
                    }

                    input[name='location'] {
                        background-image: url(/images/icons/search_icon_white.png);
                        background-repeat: no-repeat;
                        background-position: 550px;
                    }

                    label {
                        margin-bottom: 15px;
                        font-family: 'Instrument Sans';
                        font-style: normal;
                        font-weight: 500;
                        font-size: 18px;
                        line-height: 22px;
                        letter-spacing: 0.1em;
                        text-transform: uppercase;
                        font-feature-settings: 'calt' off;
                        color: white;
                    }
                }
            }
        }

        .media-upload {
            order: 3;
            flex: 0 0 auto;
            margin-top: 75px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            .view-carousel {
                flex: 0 0 auto;
                display: flex;

                .title {
                    font-family: 'Instrument Sans';
                    font-style: normal;
                    font-weight: 600;
                    font-size: 21px;
                    line-height: 24px;
                    text-align: center;
                    letter-spacing: 3px;
                    text-transform: uppercase;
                    color: #d7eb5a;
                    margin-top: 25px;
                    margin-bottom: 0px;

                    a.nostyle:link {
                        text-decoration: inherit;
                        color: inherit;
                        cursor: pointer;
                    }

                    a.nostyle:visited {
                        text-decoration: inherit;
                        color: inherit;
                        cursor: pointer;
                    }
                }
            }

            button {
                margin-bottom: 75px;
                display: flex;
                justify-content: center;
                align-items: center;
                width: 435px;
                height: 61px;
                background: #d7eb5a;
                border-radius: 40px;
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.17);
                font-family: 'Instrument Sans';
                color: #1b0d29;
                letter-spacing: 0.1em;
                text-transform: uppercase;
                font-style: normal;
                font-weight: 500;
                font-size: 18px;
                line-height: 22px;
                font-feature-settings: 'calt' off;
            }

            p {
                font-family: 'Instrument Sans';
                font-style: normal;
                font-weight: 400;
                font-size: 18px;
                line-height: 27px;
                letter-spacing: 3px;
                color: white;
                margin-top: 25px;
                margin-bottom: 75px;
            }

            p.required-message {
                margin-top: -45px;
                margin-bottom: 75px;
            }
        }

        .step-action {
            order: 4;
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            .next-step {
                order: 1;
                flex: 0 0 auto;
                display: flex;
                justify-content: center;
                align-items: center;
                background: #d7eb5a;
                border-radius: 40px;
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.17);
                width: 435px;
                height: 61px;
                color: #1b0d29;
                font-family: 'Instrument Sans';
                font-style: normal;
                font-weight: 500;
                font-size: 18px;
                line-height: 22px;
                letter-spacing: 0.1em;
                text-transform: uppercase;
                font-feature-settings: 'calt' off;
            }

            .next-step:disabled,
            .next-step[disabled] {
                background: transparent;
                color: white;
                border: 1px solid white;
            }

            .prev-step {
                order: 2;
                flex: 0 0 auto;
                font-family: Instrument Sans;
                font-style: normal;
                font-weight: 500;
                font-size: 14px;
                line-height: 16px;
                display: flex;
                align-items: center;
                text-align: center;
                letter-spacing: 1.25px;
                text-transform: uppercase;
                color: white;
                background: transparent;
                border: none;
                margin-top: 25px;
            }
        }
    }
</style>
